import _ from 'lodash';
import { useMemo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { getFormAsyncErrors, getFormSyncErrors } from 'redux-form';
/**
 * react-selectの共通ロジックを制御するuse
 * @param changeInputValueCallback
 * @param formName
 * @param fields
 * @param inputName
 * @returns {{changeInputValue: ((function(*=): void)|*), handleBlur: ((function(): void)|*), handleKeyDown: ((function(*): void)|*), viewValues: {label: *, value: *}[], inputValue: string, errorMessage: string}}
 */
export const useReactSelect = (changeInputValueCallback, formName, fields, inputName) => {
    const [inputValue, rawChangeInputValue] = useState('');
    const [isDirty, changeIsDirty] = useState(false);
    /**
     * 入力値の変更
     * @type {(function(*=): void)|*}
     */
    const changeInputValue = useCallback((v) => {
        if (changeInputValueCallback) {
            changeInputValueCallback(v);
        }
        rawChangeInputValue(v);
    }, [changeInputValueCallback, rawChangeInputValue]);
    const errors = useSelector((state) => getFormSyncErrors(formName)(state));
    const asyncErrors = useSelector((state) => getFormAsyncErrors(formName)(state));
    /**
     * エラーメッセージ
     * @type {string}
     */
    const errorMessage = useMemo(() => {
        // lodashのmergeの第一引数には破壊的な変更が行われるので、空オブジェクトを渡す
        const mergedErrors = _.merge({}, errors, asyncErrors);
        const arrayValueError = _.get(mergedErrors, fields.name) || [];
        const errorKeys = Object.keys(arrayValueError);
        for (let i = 0; i < errorKeys.length; i += 1) {
            const key = errorKeys[i];
            const v = arrayValueError[key];
            let result = '';
            if (key === '_error' && isDirty) {
                result = v;
            }
            else if (key !== '_error') {
                result = v && v[inputName];
            }
            if (result) {
                return result;
            }
        }
        return '';
    }, [asyncErrors, isDirty, fields.name, errors, inputName]);
    /**
     * 要素の追加
     * @type {(function(*): void)|*}
     */
    const fieldPush = useCallback((setValue) => {
        changeInputValue('');
        const isExist = (fields.getAll() || []).find((v) => v[inputName] === setValue);
        if (!isExist) {
            fields.push({ [inputName]: setValue });
        }
    }, [changeInputValue, fields, inputName]);
    /**
     * 要素の削除
     * @type {(function(): void)|*}
     */
    const fieldRemove = useCallback(() => {
        const index = fields.length - 1;
        if (index >= 0) {
            fields.remove(index);
        }
    }, [fields]);
    /**
     * option変更時のイベント
     * @type {(function(*, *): void)|*}
     */
    const handleChange = useCallback((values, args = {}) => {
        const { action } = args;
        switch (action) {
            case 'create-option': {
                // 新規追加の場合
                const { value: keyword } = values.find(({ __isNew__: isNew }) => isNew) || {};
                fieldPush(keyword);
                break;
            }
            case 'select-option': {
                // 選択された場合
                const { option: { value }, } = args;
                fieldPush(value);
                break;
            }
            case 'pop-value': {
                // 削除された場合
                fieldRemove();
                break;
            }
            default: {
                break;
            }
        }
    }, [fieldPush, fieldRemove]);
    /**
     * Blur時のイベント
     * @type {(function(): void)|*}
     */
    const handleBlur = useCallback(() => {
        changeIsDirty(true);
        if (!inputValue) {
            return;
        }
        fieldPush(inputValue);
    }, [changeIsDirty, fieldPush, inputValue]);
    const viewValues = useMemo(() => (fields.getAll() || []).map((v) => ({
        label: v[inputName],
        value: v[inputName],
    })), [inputName, fields]);
    return {
        changeInputValue,
        errorMessage,
        fieldPush,
        fieldRemove,
        handleBlur,
        handleChange,
        inputValue,
        viewValues,
    };
};
export default useReactSelect;
